import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    ReactFlow,
    ReactFlowProvider,
    Background,
    Controls,
    Handle,
    Position,
    useNodesState,
    useEdgesState,
    useReactFlow,
    useNodesInitialized,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { FaStar, FaRegStar, FaCubes, FaBriefcase, FaDesktop, FaChevronRight, FaChevronDown, FaFolder, FaRegFile, FaProjectDiagram } from "react-icons/fa";
import { Skeleton } from '@mui/material'
import { DashboardContext } from "../../context/DashboardContext";
import { AuthContext } from "../../context/AuthContext";

const NOME_GRAFICO = "Relacionamentos";

// Cores, ícones e nome da pasta por tipo (o campo "tipo" vem do ReactFlowDataDTO da API)
const TIPOS = {
    Capacidade: { nome: "Capacidade de Negócio", pasta: "Capacidade de Negócio", cor: "#2563eb", fundo: "#dbeafe", Icone: FaCubes },
    Servico: { nome: "Serviço", pasta: "Serviços", cor: "#16a34a", fundo: "#dcfce7", Icone: FaBriefcase },
    Aplicacao: { nome: "Aplicação", pasta: "Aplicações", cor: "#9333ea", fundo: "#f3e8ff", Icone: FaDesktop },
};

// Handles no centro do nó para as arestas saírem em "estrela"
const HANDLE_CENTRO = { top: "50%", left: "50%", transform: "translate(-50%, -50%)", opacity: 0 };

function NoElemento({ data }) {
    const tipo = TIPOS[data.tipo] || { cor: "#64748b", fundo: "#f1f5f9", Icone: FaProjectDiagram };
    const { Icone } = tipo;

    return (
        <div
            title={data.descricao}
            style={{
                background: tipo.fundo,
                border: `${data.centro ? 2 : 1}px solid ${tipo.cor}`,
                borderRadius: '6px',
                padding: data.centro ? '8px 14px' : '6px 12px',
                fontSize: data.centro ? '14px' : '13px',
                maxWidth: '240px',
                cursor: data.centro ? 'default' : 'pointer',
                color: '#1f2937',
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                fontWeight: data.centro ? 600 : 400,
            }}
        >
            <Handle type="target" position={Position.Top} style={HANDLE_CENTRO} isConnectable={false} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Icone size={13} color={tipo.cor} style={{ flexShrink: 0 }} />
                <span style={{ lineHeight: 1.2 }}>{data.label}</span>
            </div>
            <Handle type="source" position={Position.Bottom} style={HANDLE_CENTRO} isConnectable={false} />
        </div>
    );
}

const nodeTypes = { elemento: NoElemento };

// Monta o elemento selecionado no centro e os vinculados a ele em círculo (comportamento do Visualiser do Archi)
const montarLayoutRadial = (centroId, nosOriginais, arestasOriginais) => {
    const vizinhos = new Set();
    arestasOriginais.forEach((a) => {
        if (a.source === centroId) vizinhos.add(a.target);
        if (a.target === centroId) vizinhos.add(a.source);
    });

    const centro = nosOriginais.find((n) => n.id === centroId);
    const listaVizinhos = nosOriginais.filter((n) => vizinhos.has(n.id));
    // Raio cresce com a quantidade de vizinhos para os nós não se sobreporem (~220px de arco por nó)
    const raio = Math.max(220, (listaVizinhos.length * 220) / (2 * Math.PI));

    const nos = [
        { id: centro.id, type: "elemento", position: { x: 0, y: 0 }, data: { ...centro.data, centro: true } },
        ...listaVizinhos.map((n, i) => {
            const angulo = (2 * Math.PI * i) / listaVizinhos.length - Math.PI / 2;
            return {
                id: n.id,
                type: "elemento",
                position: { x: Math.cos(angulo) * raio, y: Math.sin(angulo) * raio },
                data: n.data,
            };
        }),
    ];

    const arestas = arestasOriginais
        .filter((a) => a.source === centroId || a.target === centroId)
        .map((a) => ({ id: a.id, source: a.source, target: a.target, type: "straight", style: { stroke: '#9ca3af' } }));

    return { nos, arestas };
};

function ArvoreLateral({ nos, selecionadoId, onSelecionar }) {
    const [abertas, setAbertas] = useState([]); // pastas começam fechadas

    const grupos = useMemo(
        () => Object.keys(TIPOS).map((tipo) => ({ tipo, itens: nos.filter((n) => n.data?.tipo === tipo) })),
        [nos]
    );

    const alternarPasta = (tipo) =>
        setAbertas((atual) => (atual.includes(tipo) ? atual.filter((t) => t !== tipo) : [...atual, tipo]));

    return (
        <div style={{ width: '300px', flexShrink: 0, maxHeight: '70vh', overflowY: 'auto', paddingRight: '8px' }}>
            {grupos.map(({ tipo, itens }) => {
                const aberta = abertas.includes(tipo);
                return (
                    <div key={tipo} style={{ marginBottom: '4px' }}>
                        <button
                            onClick={() => alternarPasta(tipo)}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', background: 'none', border: 'none', padding: '6px 4px', fontSize: '15px', color: 'inherit', textAlign: 'left' }}
                        >
                            {aberta ? <FaChevronDown size={11} color="#6b7280" /> : <FaChevronRight size={11} color="#6b7280" />}
                            <FaFolder size={16} color="#60a5fa" />
                            <span>{TIPOS[tipo].pasta}</span>
                            <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#9ca3af' }}>{itens.length}</span>
                        </button>
                        {aberta && (
                            <div style={{ borderLeft: '1px solid #e5e7eb', marginLeft: '9px' }}>
                                {itens.length === 0 && (
                                    <div style={{ padding: '4px 16px', fontSize: '13px', color: '#9ca3af' }}>Nenhum item</div>
                                )}
                                {itens.map((item) => {
                                    const selecionado = item.id === selecionadoId;
                                    return (
                                        <button
                                            key={item.id}
                                            onClick={() => onSelecionar(item.id)}
                                            title={item.data?.descricao}
                                            className="relacionamentos-item"
                                            data-selecionado={selecionado}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                                                border: 'none', padding: '6px 16px', fontSize: '14px', textAlign: 'left',
                                                color: 'inherit', borderRadius: '4px',
                                                background: selecionado ? TIPOS[tipo].fundo : 'transparent',
                                                fontWeight: selecionado ? 600 : 400,
                                            }}
                                        >
                                            <FaRegFile size={13} color="#6b7280" style={{ flexShrink: 0 }} />
                                            <span>{item.data?.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function Fluxo({ grafo, centroId, onFocar }) {
    const { fitView } = useReactFlow();
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const precisaEnquadrar = useRef(false);

    useEffect(() => {
        const { nos, arestas } = montarLayoutRadial(centroId, grafo.nodes, grafo.edges);
        setNodes(nos);
        setEdges(arestas);
        precisaEnquadrar.current = true;
    }, [grafo, centroId]);

    // Enquadra só depois que o React Flow mediu os nós da nova visão
    const nosMedidos = useNodesInitialized();
    useEffect(() => {
        if (!precisaEnquadrar.current || !nosMedidos || !nodes.length) return;
        precisaEnquadrar.current = false;
        fitView({ duration: 500, padding: 0.2, maxZoom: 1.2 });
    }, [nodes, nosMedidos]);

    const focarNo = useCallback((_, node) => {
        if (node.id !== centroId) onFocar(node.id);
    }, [centroId, onFocar]);

    return (
        <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={focarNo}
            nodeOrigin={[0.5, 0.5]}
            nodesConnectable={false}
            minZoom={0.1}
            proOptions={{ hideAttribution: true }}
        >
            <Background />
            <Controls showInteractive={false} />
        </ReactFlow>
    );
}

const estiloLink = (ativo) => ({
    background: 'none',
    border: 'none',
    padding: 0,
    fontSize: '13px',
    fontWeight: ativo ? 600 : 400,
    color: ativo ? 'inherit' : '#6b7280',
    cursor: ativo ? 'default' : 'pointer',
});

export default function Relacionamentos({ corFavorito }) {
    const { getArvoreFluxo, ArvoreFluxo, AdicionarGraficoFavorito } = useContext(DashboardContext)
    const { EmpresaKey } = useContext(AuthContext)
    const [isLoading, setIsLoading] = useState(false)
    const [historico, setHistorico] = useState([]) // ids dos elementos que já foram o centro, para o caminho de navegação

    const centroAtual = historico[historico.length - 1];
    const grafo = useMemo(() => ({ nodes: ArvoreFluxo?.nodes || [], edges: ArvoreFluxo?.edges || [] }), [ArvoreFluxo]);
    const nomeDoNo = (id) => grafo.nodes.find((n) => n.id === id)?.data?.label || id;

    useEffect(() => {
        setIsLoading(true)
        setHistorico([])
        getArvoreFluxo()
            .catch(() => { })
            .finally(() => setIsLoading(false))
    }, [EmpresaKey])

    // Clique na árvore começa uma nova navegação; clique no gráfico continua a atual
    const selecionarNaArvore = (id) => setHistorico([id]);
    const focarNoGrafico = useCallback((id) => setHistorico((h) => [...h, id]), []);

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between', padding: '0' }}>
                <h2 className="h2-bsc">
                    <button onClick={() => AdicionarGraficoFavorito(NOME_GRAFICO)} className='btn-grafico'>{corFavorito(NOME_GRAFICO) ? <FaRegStar /> : <FaStar style={{ color: 'orange' }} />}</button>
                    <span style={{ fontWeight: '500' }}>Relacionamentos</span>
                </h2>
            </div>
            <div>
                {isLoading ?
                    <Skeleton variant="rounded" width="100%" height={350} />
                    :
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'stretch' }}>
                        <ArvoreLateral nos={grafo.nodes} selecionadoId={centroAtual} onSelecionar={selecionarNaArvore} />

                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '8px', minHeight: '22px', marginBottom: '6px' }}>
                                {/* Caminho percorrido: elemento > elemento... */}
                                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
                                    {historico.map((id, i) => (
                                        <span key={`${id}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            {i > 0 && <FaChevronRight size={9} color="#9ca3af" />}
                                            <button onClick={() => setHistorico((h) => h.slice(0, i + 1))} style={estiloLink(i === historico.length - 1)}>
                                                {nomeDoNo(id)}
                                            </button>
                                        </span>
                                    ))}
                                </div>

                                {/* Legenda dos tipos */}
                                <div style={{ display: 'flex', gap: '14px', marginLeft: 'auto' }}>
                                    {Object.entries(TIPOS).map(([chave, { nome, cor, fundo, Icone }]) => (
                                        <span key={chave} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#4b5563' }}>
                                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px', borderRadius: '4px', background: fundo, border: `1px solid ${cor}` }}>
                                                <Icone size={11} color={cor} />
                                            </span>
                                            {nome}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            <div style={{ height: '65vh', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                                {centroAtual ?
                                    <ReactFlowProvider>
                                        <Fluxo grafo={grafo} centroId={centroAtual} onFocar={focarNoGrafico} />
                                    </ReactFlowProvider>
                                    :
                                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '14px' }}>
                                        {grafo.nodes.length ? "Selecione um item ao lado para ver seus relacionamentos." : "Nenhum relacionamento encontrado."}
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                }
            </div>

            <style>{`
                .relacionamentos-item:not([data-selecionado="true"]):hover { background: #f3f4f6 !important; }
                .react-flow__node-elemento { transition: transform 400ms ease; }
                .react-flow__node-elemento.dragging { transition: none; }
            `}</style>
        </>
    );
}
